/**
 * Pathfinding and movement (R14, KTD6).
 *
 * Three mechanisms, in decreasing scope:
 *
 *  1. **A cluster graph** partitions the map into 16×16 blocks and labels the
 *     connected components inside each. It answers "is this destination
 *     reachable" in O(clusters) rather than O(cells), and it is rebuilt
 *     per-cluster when passability changes — which is what makes a destroyed
 *     wall restore a previously blocked route.
 *  2. **Shared flow fields** move groups. A group ordered to one destination
 *     shares a single field, so a forty-unit order costs one search rather
 *     than forty.
 *  3. **Cell reservation** handles local avoidance. A unit claims its next
 *     cell; a unit whose claim fails waits, then repaths, then scatters.
 *
 * *A documented deviation from KTD6.* The plan specifies A* over the cluster
 * graph refined per-cluster on demand. The cluster graph is here and does the
 * reachability and incremental-rebuild work it was chosen for, but the flow
 * field is computed over the **whole grid** rather than lazily per cluster. At
 * 128×128 a full Dijkstra is about a tenth of a millisecond, so corridor
 * restriction buys no measurable time while introducing local minima at the
 * corridor boundary — and a unit stuck in a local minimum is exactly the
 * deadlock class AE5 exists to catch. The hierarchy earns its place for
 * reachability; it does not earn it for the field.
 *
 * Everything here is integer. Headings come from `fix.atan2`, never `Math`.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { CLUSTER_SIZE, MAP_SIZE, MAX_ENTITIES } from '../core/config.js'
import { World, CELL } from './world.js'
import { Entities, EF, ST, NO_ENTITY } from './entities.js'
import { CMD, type Command, REASON } from '../net/commands.js'
import { defById } from './data/entities.js'

const NO_CELL = -1

/** Eight-neighbour offsets, orthogonals first so ties prefer straight lines. */
const DX = [1, -1, 0, 0, 1, 1, -1, -1]
const DY = [0, 0, 1, -1, 1, -1, 1, -1]
const STEP_COST = [10, 10, 10, 10, 14, 14, 14, 14]
const NO_DIR = 255

/** Ticks a blocked unit waits before recomputing its own route. */
const WAIT_BEFORE_REPATH = 12
/** Consecutive repath failures before a unit scatters. */
const FAILS_BEFORE_SCATTER = 3
/** Ticks a scatter lasts. */
const SCATTER_TICKS = 20
/** Lattice spacing for coarse (combat approach) destinations, in cells. */
const COARSE = 6
/** Legs a unit can have queued behind its current goal. */
const MAX_WAYPOINTS = 8
const MAX_DETOUR = 32

/** How close to a cell centre counts as arrived, in Q16.16. */
const ARRIVE_EPSILON = fix.fromFloat(0.06)

interface FlowField {
  dest: number
  /** Step cost to the destination, or UNREACHABLE. */
  dist: Int32Array
  /** Index into DX/DY toward the destination, or NO_DIR. */
  dir: Uint8Array
  /** Passability generation this field was built against. */
  generation: number
  /** Tick it was last used, for eviction. */
  lastUsed: number
  refs: number
}

const UNREACHABLE = 0x7fffffff

/**
 * Cells from a taken destination at which a unit calls it arrived. Two is
 * enough for a group to settle into a blob rather than a queue, and small
 * enough that "go here" still means here.
 */
const ARRIVAL_RADIUS = 2

/** Ticks a unit keeps trying for a taken goal before settling for nearby. */
const ARRIVAL_PATIENCE = 45

export class Pathfind implements Subsystem {
  static id = 'pathfind'
  static deps = ['world', 'entities'] as const

  private ctx!: Ctx
  private world!: World
  private entities!: Entities

  // --- occupancy and reservation ------------------------------------------
  /** Cell each unit currently owns, or NO_CELL. */
  readonly unitCell = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  /** `slot + 1` of the unit owning each cell, or 0. */
  readonly cellUnit = new Int32Array(MAP_SIZE * MAP_SIZE)
  /** Ticks a unit has been blocked. Raises its claim priority. */
  readonly wait = new Int32Array(MAX_ENTITIES)
  /** Consecutive repath failures. */
  readonly fails = new Int32Array(MAX_ENTITIES)
  /** Destination cell per unit, or NO_CELL. */
  readonly goal = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  private readonly arrivalRadius = new Int32Array(MAX_ENTITIES).fill(ARRIVAL_RADIUS)
  private readonly detourSteps = new Int32Array(MAX_ENTITIES * MAX_DETOUR)
  private readonly detourLen = new Int32Array(MAX_ENTITIES)
  private readonly localParents = new Int32Array(21 * 21)
  private readonly localQueue = new Int32Array(21 * 21)
  /**
   * Cells queued behind the current goal, oldest first, per unit.
   *
   * A fixed ring rather than a list per unit: this is simulation state and has
   * to hash, and a typed array of a known size hashes trivially where an array
   * of arrays does not.
   */
  private readonly waypoints = new Int32Array(MAX_ENTITIES * MAX_WAYPOINTS).fill(NO_CELL)
  private readonly waypointLen = new Int32Array(MAX_ENTITIES)
  /** Scatter countdown. */
  readonly scatter = new Int32Array(MAX_ENTITIES)

  private readonly desired = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  private readonly claim = new Int32Array(MAP_SIZE * MAP_SIZE)
  private readonly movers: number[] = []

  // --- flow fields ---------------------------------------------------------
  private readonly fields = new Map<number, FlowField>()
  private readonly fieldOf = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  private passabilityGeneration = 0
  /** Instrumented so a test can assert a group order costs one search. */
  pathRequests = 0
  fieldBuilds = 0

  private readonly maxFields = 64

  // --- cluster graph -------------------------------------------------------
  /** Component label per cell, unique across the map. 0 means impassable. */
  private readonly component = new Int32Array(MAP_SIZE * MAP_SIZE)
  /** Union-find over component labels, merged across cluster borders. */
  private region = new Int32Array(1)
  /** Cells per union root, rebuilt with the cluster graph. */
  private regionCells = new Int32Array(1)
  private componentCount = 0

  // Dijkstra scratch, allocated once.
  private readonly heap = new Int32Array(MAP_SIZE * MAP_SIZE * 2)
  private heapSize = 0
  private readonly scratchDist = new Int32Array(MAP_SIZE * MAP_SIZE)

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')

    ctx.hash.register('pathfind:unitCell', this.unitCell)
    ctx.hash.register('pathfind:cellUnit', this.cellUnit)
    ctx.hash.register('pathfind:wait', this.wait)
    ctx.hash.register('pathfind:fails', this.fails)
    ctx.hash.register('pathfind:goal', this.goal)
    ctx.hash.register('pathfind:arrivalRadius', this.arrivalRadius)
    ctx.hash.register('pathfind:detourSteps', this.detourSteps)
    ctx.hash.register('pathfind:detourLen', this.detourLen)
    ctx.hash.register('pathfind:waypoints', this.waypoints)
    ctx.hash.register('pathfind:waypointLen', this.waypointLen)
    ctx.hash.register('pathfind:scatter', this.scatter)
    ctx.hash.register('pathfind:nearTicks', this.nearTicks)

    ctx.on('actor:spawn', (p) => this.onSpawn(p as { id: number }))
    ctx.on('actor:removed', (p) => this.onRemoved(p as { slot: number }))
    ctx.on('world:passability', () => this.invalidatePassability())

    this.rebuildClusters()
  }

  // --- registration --------------------------------------------------------

  private onSpawn(p: { id: number }): void {
    const slot = this.entities.resolve(p.id)
    if (slot < 0) return
    if (!this.entities.has(slot, EF.Mobile)) return
    const cell = this.world.cellAt(fix.raw(this.entities.x[slot]), fix.raw(this.entities.y[slot]))
    this.place(slot, cell)
  }

  private onRemoved(p: { slot: number }): void {
    this.release(p.slot)
  }

  /** Put a unit on a cell, taking the nearest free one if it is taken. */
  place(slot: number, cell: number): boolean {
    this.release(slot)
    if (this.entities.has(slot, EF.Air)) {
      // Aircraft occupy no cell. `unitCell` still tracks which cell they are
      // *over*, because everything that asks "where is this unit" reads it, but
      // nothing is reserved: ground units path underneath, and a landing pad
      // full of gunships does not wall the base in.
      if (cell < 0) return false
      this.unitCell[slot] = cell
      this.entities.x[slot] = this.world.cellCenterX(cell % MAP_SIZE)
      this.entities.y[slot] = this.world.cellCenterY((cell / MAP_SIZE) | 0)
      return true
    }
    const free = this.nearestFreeCell(cell)
    if (free < 0) return false
    this.unitCell[slot] = free
    this.cellUnit[free] = slot + 1
    const cx = free % MAP_SIZE
    const cy = (free / MAP_SIZE) | 0
    this.entities.x[slot] = this.world.cellCenterX(cx)
    this.entities.y[slot] = this.world.cellCenterY(cy)
    return true
  }

  release(slot: number): void {
    this.detourLen[slot] = 0
    this.clearWaypoints(slot)
    const cell = this.unitCell[slot]
    if (cell !== NO_CELL && this.cellUnit[cell] === slot + 1) this.cellUnit[cell] = 0
    this.unitCell[slot] = NO_CELL
    this.goal[slot] = NO_CELL
    this.wait[slot] = 0
    this.fails[slot] = 0
    this.scatter[slot] = 0
    this.fieldOf[slot] = NO_CELL
  }

  /**
   * The cell closest to `goalCell` that `fromCell` can actually walk to.
   *
   * A breadth-first sweep of the unit's own component, keeping the best cell
   * seen. Bounded by the component, so on a connected map this is one pass over
   * the grid — paid only when an order was going to be refused outright.
   */
  private nearestReachableTo(fromCell: number, goalCell: number): number {
    if (fromCell < 0 || goalCell < 0) return -1
    const gx = goalCell % MAP_SIZE
    const gy = (goalCell / MAP_SIZE) | 0

    const seen = this.approachSeen
    seen.fill(0)
    const queue = this.approachQueue
    let head = 0
    let tail = 0
    queue[tail++] = fromCell
    seen[fromCell] = 1

    let best = fromCell
    let bestD = Infinity
    while (head < tail) {
      const cell = queue[head++]
      const cx = cell % MAP_SIZE
      const cy = (cell / MAP_SIZE) | 0
      const d = (cx - gx) * (cx - gx) + (cy - gy) * (cy - gy)
      if (d < bestD) {
        bestD = d
        best = cell
      }
      for (let dir = 0; dir < 4; dir++) {
        const nx = cx + (dir === 0 ? 1 : dir === 1 ? -1 : 0)
        const ny = cy + (dir === 2 ? 1 : dir === 3 ? -1 : 0)
        if (nx < 0 || ny < 0 || nx >= MAP_SIZE || ny >= MAP_SIZE) continue
        const ni = ny * MAP_SIZE + nx
        if (seen[ni] !== 0) continue
        seen[ni] = 1
        if (!this.isPassable(ni)) continue
        queue[tail++] = ni
      }
    }
    return best
  }

  /** Ticks spent hovering next to a goal another unit is standing on. */
  private readonly nearTicks = new Int32Array(MAX_ENTITIES)
  private readonly approachSeen = new Uint8Array(MAP_SIZE * MAP_SIZE)
  private readonly approachQueue = new Int32Array(MAP_SIZE * MAP_SIZE)

  /** Nearest cell that is passable and unoccupied, searched in rings. */
  nearestFreeCell(cell: number, maxRadius = 12): number {
    if (cell < 0) return -1
    const sx = cell % MAP_SIZE
    const sy = (cell / MAP_SIZE) | 0
    if (this.isCellFree(sx, sy)) return cell
    for (let r = 1; r <= maxRadius; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          // Ring only: skip the interior, which earlier radii already covered.
          if (dx > -r && dx < r && dy > -r && dy < r) continue
          const x = sx + dx
          const y = sy + dy
          if (this.isCellFree(x, y)) return y * MAP_SIZE + x
        }
      }
    }
    return -1
  }

  isCellFree(cx: number, cy: number): boolean {
    if (cx < 0 || cy < 0 || cx >= MAP_SIZE || cy >= MAP_SIZE) return false
    const i = cy * MAP_SIZE + cx
    if ((this.world.flags[i] & CELL.Passable) === 0) return false
    if (this.world.occupancy[i] !== NO_ENTITY) return false
    return this.cellUnit[i] === 0
  }

  private isPassable(i: number): boolean {
    return (this.world.flags[i] & CELL.Passable) !== 0 && this.world.occupancy[i] === NO_ENTITY
  }

  // --- commands ------------------------------------------------------------

  handleCommand(cmd: Command): boolean {
    switch (cmd.t) {
      case CMD.Move:
      case CMD.AttackMove: {
        const cell = this.world.cellAt(cmd.x, cmd.y)
        if (cell < 0) return true
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot >= 0) this.clearWaypoints(slot)
        }
        this.orderGroup(cmd.units, cell, cmd.t === CMD.AttackMove ? ST.AttackMoving : ST.Moving)
        return true
      }
      case CMD.Waypoint: {
        const cell = this.world.cellAt(fix.raw(cmd.x), fix.raw(cmd.y))
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot < 0) continue
          // The first leg starts immediately; the rest wait their turn. Without
          // this a route only begins once the player happens to issue a plain
          // move as well, which is not what adding a waypoint means.
          if (this.goal[slot] === NO_CELL) this.orderGroup([id], cell, ST.Moving)
          else this.pushWaypoint(slot, cell)
        }
        return true
      }
      case CMD.Stop: {
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot < 0) continue
          this.stop(slot)
        }
        return true
      }
      case CMD.Scatter: {
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot < 0) continue
          this.stop(slot)
          this.entities.state[slot] = ST.Scattering
          this.beginScatter(slot)
        }
        return true
      }
      default:
        return false
    }
  }

  /**
   * Snap a destination to a coarse lattice so nearby orders share one field.
   *
   * A flow field is per *destination*, so four hundred units each closing on
   * their own target is four hundred full-grid searches per tick — the
   * profiler measured 35 ms of a 35 ms tick here. Combat approach does not need
   * cell precision: units stop at weapon range anyway, and snapping to a
   * six-cell lattice collapses an entire engagement onto a handful of fields.
   */
  coarseDestination(cell: number): number {
    if (cell < 0) return cell
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    const qx = Math.min(MAP_SIZE - 1, ((cx / COARSE) | 0) * COARSE + (COARSE >> 1))
    const qy = Math.min(MAP_SIZE - 1, ((cy / COARSE) | 0) * COARSE + (COARSE >> 1))
    return qy * MAP_SIZE + qx
  }

  /**
   * Order a group to one destination. **One flow field, not N paths** — this is
   * the whole reason flow fields are the group-movement answer.
   */
  orderGroup(units: ArrayLike<number>, requestedCell: number, state: number): void {
    // Aircraft need no field: nothing is in their way, so "can it get there" is
    // never a question and the destination is wherever the player pointed.
    const airborne: number[] = []
    const grounded: number[] = []
    for (let i = 0; i < units.length; i++) {
      const slot = this.entities.resolve(units[i] as number)
      if (slot < 0) continue
      if (!this.entities.has(slot, EF.Mobile)) continue
      // A dug-in squad has traded away the ability to move. Silently dropping
      // it from the group rather than refusing the whole order is deliberate:
      // a player box-selecting an army that happens to contain one dug-in
      // squad still wants the rest of the army to go.
      if (this.entities.has(slot, EF.DugIn)) continue
      ;(this.entities.has(slot, EF.Air) ? airborne : grounded).push(slot)
    }
    for (const slot of airborne) this.orderAir(slot, requestedCell, state)
    if (grounded.length === 0) return
    units = grounded.map((slot) => this.entities.idAt(slot))

    const field = this.acquireField(requestedCell)
    if (!field) {
      // An unreachable destination fails visibly rather than hanging (R14).
      for (let i = 0; i < units.length; i++) {
        const slot = this.entities.resolve(units[i] as number)
        if (slot >= 0) this.stop(slot)
      }
      this.ctx.emit('order:rejected', { reason: REASON.Unreachable, cell: requestedCell })
      return
    }

    // The field may have resolved a blocked destination to a nearby passable
    // cell; every unit follows the resolved one so they agree on where "there" is.
    const destCell = field.dest
    const radius = state === ST.Moving
      ? Math.max(ARRIVAL_RADIUS, Math.ceil(Math.sqrt(grounded.length / 2)))
      : ARRIVAL_RADIUS

    for (let i = 0; i < units.length; i++) {
      const slot = this.entities.resolve(units[i] as number)
      if (slot < 0) continue
      if (!this.entities.has(slot, EF.Mobile)) continue

      const from = this.unitCell[slot]
      let target = destCell
      let unitField = field
      if (from >= 0 && field.dist[from] === UNREACHABLE) {
        // This unit cannot reach the destination: it is in a different pocket
        // of the map, or — far more often — the destination resolved to a cell
        // enclosed by the enemy's own buildings.
        //
        // Get as close as possible rather than refusing. Refusing looks correct
        // and is not: a bot sweep produced a player whose 142-unit army stood
        // idle for twenty simulated minutes because the cell the order resolved
        // to was inside a courtyard, and a human given that order walks up to
        // the wall and attacks it.
        const approach = this.nearestReachableTo(from, destCell)
        const alt = approach >= 0 ? this.acquireField(approach) : null
        // Still announced, either way. The destination *is* unreachable and the
        // player is owed that (R14, R33); the difference is that the unit now
        // walks up to it instead of standing still.
        this.ctx.emit('order:rejected', {
          owner: this.entities.owner[slot],
          reason: REASON.Unreachable,
        })
        if (!alt || alt.dist[from] === UNREACHABLE) {
          this.stop(slot)
          continue
        }
        unitField = alt
        target = alt.dest
      }
      void unitField

      this.goal[slot] = target
      this.arrivalRadius[slot] = radius
      this.detourLen[slot] = 0
      this.fieldOf[slot] = target
      this.entities.state[slot] = state
      this.entities.orderX[slot] = this.world.cellCenterX(target % MAP_SIZE)
      this.entities.orderY[slot] = this.world.cellCenterY((target / MAP_SIZE) | 0)
      this.wait[slot] = 0
      this.fails[slot] = 0
      this.scatter[slot] = 0
      this.nearTicks[slot] = 0
    }
  }

  /** Point an aircraft at a cell. No field, no reachability, no claim. */
  private orderAir(slot: number, cell: number, state: number): void {
    this.goal[slot] = cell
    this.fieldOf[slot] = NO_CELL
    this.entities.state[slot] = state
    this.entities.orderX[slot] = this.world.cellCenterX(cell % MAP_SIZE)
    this.entities.orderY[slot] = this.world.cellCenterY((cell / MAP_SIZE) | 0)
    this.wait[slot] = 0
    this.fails[slot] = 0
    this.scatter[slot] = 0
  }

  /** Order a single aircraft, for the sim's own orders (a return-to-base trip). */
  orderAircraft(slot: number, cell: number, state: number): void {
    this.orderAir(slot, cell, state)
  }

  /** Append a leg to a unit's route. Silently full is better than truncating mid-path. */
  pushWaypoint(slot: number, cell: number): void {
    const len = this.waypointLen[slot]
    if (len >= MAX_WAYPOINTS) return
    this.waypoints[slot * MAX_WAYPOINTS + len] = cell
    this.waypointLen[slot] = len + 1
  }

  waypointCount(slot: number): number {
    return this.waypointLen[slot]
  }

  /** Read the current cached path for the selected-unit overlay without running a search. */
  traceRoute(slot: number, out: Int32Array): number {
    let cell = this.unitCell[slot]
    const goal = this.goal[slot]
    if (goal < 0 || cell < 0 || out.length < 2) return 0
    out[0] = cell
    if (this.entities.has(slot, EF.Air)) {
      out[1] = goal
      return 2
    }
    const field = this.fields.get(this.fieldOf[slot])
    if (!field || field.generation !== this.passabilityGeneration) return 0
    let count = 1
    for (let i = this.detourLen[slot] - 1; i >= 0 && count < out.length; i--) {
      cell = this.detourSteps[slot * MAX_DETOUR + i]
      if (out[count - 1] !== cell) out[count++] = cell
    }
    while (cell !== goal && count < out.length) {
      const direction = field.dir[cell]
      if (direction === NO_DIR) break
      cell += DY[direction] * MAP_SIZE + DX[direction]
      out[count++] = cell
    }
    return count
  }

  /** Take the next leg, shifting the rest down. Returns `NO_CELL` when empty. */
  private shiftWaypoint(slot: number): number {
    const len = this.waypointLen[slot]
    if (len <= 0) return NO_CELL
    const base = slot * MAX_WAYPOINTS
    const next = this.waypoints[base]
    for (let i = 0; i < len - 1; i++) this.waypoints[base + i] = this.waypoints[base + i + 1]
    this.waypoints[base + len - 1] = NO_CELL
    this.waypointLen[slot] = len - 1
    return next
  }

  private clearWaypoints(slot: number): void {
    if (this.waypointLen[slot] === 0) return
    const base = slot * MAX_WAYPOINTS
    for (let i = 0; i < MAX_WAYPOINTS; i++) this.waypoints[base + i] = NO_CELL
    this.waypointLen[slot] = 0
  }

  /**
   * Called where a unit reaches its goal: continue the route, or go idle.
   *
   * Returns true when another leg was started, so the caller knows not to also
   * mark the unit idle — a unit that is told it has arrived *and* handed a new
   * goal on the same tick flickers between states and never leaves.
   */
  private advanceRoute(slot: number): boolean {
    const next = this.shiftWaypoint(slot)
    if (next === NO_CELL) return false
    this.orderGroup([this.entities.idAt(slot)], next, ST.Moving)
    return true
  }

  /**
   * Halt in place without abandoning the order: state and waypoints survive.
   *
   * This is the attack-move pause. The unit stands to fight, and combat —
   * which is what knows when nothing is left in range — re-issues the march.
   */
  pause(slot: number): void {
    this.detourLen[slot] = 0
    this.goal[slot] = NO_CELL
    this.fieldOf[slot] = NO_CELL
    this.wait[slot] = 0
    this.fails[slot] = 0
    this.scatter[slot] = 0
    this.nearTicks[slot] = 0
    this.entities.vx[slot] = 0
    this.entities.vy[slot] = 0
  }

  stop(slot: number): void {
    this.detourLen[slot] = 0
    // An explicit stop, or a failed order, abandons the whole route rather than
    // skipping to the next leg. Anything else means a unit told to stop carries
    // on walking, which reads as the order being ignored.
    this.clearWaypoints(slot)
    this.goal[slot] = NO_CELL
    this.fieldOf[slot] = NO_CELL
    this.wait[slot] = 0
    this.fails[slot] = 0
    this.scatter[slot] = 0
    this.nearTicks[slot] = 0
    this.entities.vx[slot] = 0
    this.entities.vy[slot] = 0
    if (this.entities.state[slot] !== ST.Dead) this.entities.state[slot] = ST.Idle
  }

  private beginScatter(slot: number): void {
    this.scatter[slot] = SCATTER_TICKS
    this.detourLen[slot] = 0
  }

  // --- per-tick movement ---------------------------------------------------

  fixedUpdate(): void {
    this.movers.length = 0
    const limit = this.entities.limit

    // Phase 1: every mobile unit that wants to move picks a next cell.
    for (let slot = 0; slot < limit; slot++) {
      this.desired[slot] = NO_CELL
      if (!this.entities.isAlive(slot)) continue
      if (!this.entities.has(slot, EF.Mobile)) continue
      if (this.entities.has(slot, EF.Air)) {
        this.airStep(slot)
        continue
      }
      const cell = this.unitCell[slot]
      if (cell === NO_CELL) continue

      if (this.scatter[slot] > 0) {
        this.scatter[slot]--
        this.desired[slot] = this.scatterStep(cell)
        if (this.desired[slot] !== NO_CELL) this.movers.push(slot)
        continue
      }

      const goal = this.goal[slot]
      if (goal === NO_CELL) {
        if (this.entities.state[slot] === ST.Scattering) this.arrive(slot)
        continue
      }

      // Arrived? A unit stops on its own cell, not on the exact centre pixel.
      if (cell === goal) {
        this.arrive(slot)
        continue
      }

      // Close enough, once it is clear the goal itself is not going to free up.
      //
      // Twelve units ordered to one cell can only ever put one of them on it.
      // Without this the other eleven never *arrive*: they path around each
      // other indefinitely, a few cells short, which reads as a milling crowd
      // and — because arrival is what hands a unit back to combat — leaves an
      // army standing just outside its own weapon range of the base it was sent
      // to attack. Probing a stalled match found exactly that: twelve attackers,
      // closest approach 5.8 cells, guns that reach 5.5, and every enemy
      // structure at full health.
      //
      // The patience matters as much as the radius. Giving up the instant the
      // goal is occupied breaks the case where it is *about* to be free — two
      // units swapping places is a single order away from that — so a unit
      // spends a second and a half trying before it settles for nearby.
      if (this.cellUnit[goal] !== 0 && this.withinArrival(cell, goal, this.arrivalRadius[slot])) {
        if (++this.nearTicks[slot] >= ARRIVAL_PATIENCE) {
          this.arrive(slot)
          continue
        }
      } else {
        this.nearTicks[slot] = 0
      }

      // Still sliding between cells: keep sliding, do not claim another.
      if (!this.atCellCentre(slot, cell)) {
        this.slide(slot, cell)
        continue
      }

      const next = this.nextCell(slot, cell, goal)
      if (next === NO_CELL) {
        this.blocked(slot)
        continue
      }
      this.desired[slot] = next
      this.movers.push(slot)
    }

    // Phase 2: resolve competing claims.
    this.resolveClaims()

    // Phase 3: commit, iterating so that a column of units all step forward in
    // one tick rather than each waiting a tick for the one ahead.
    this.commitMoves()
  }

  /**
   * Fly straight at the order position. No cells, no claims, no neighbours —
   * the entire reason air units exist is that none of that applies to them.
   */
  private airStep(slot: number): void {
    const goal = this.goal[slot]
    if (goal === NO_CELL) {
      this.entities.vx[slot] = 0
      this.entities.vy[slot] = 0
      return
    }

    const speed = this.speedOf(slot)
    const tx = fix.raw(this.entities.orderX[slot])
    const ty = fix.raw(this.entities.orderY[slot])
    const dx = fix.sub(tx, fix.raw(this.entities.x[slot]))
    const dy = fix.sub(ty, fix.raw(this.entities.y[slot]))
    const d = fix.length(dx, dy)

    if (d <= speed || d === 0) {
      this.entities.x[slot] = tx
      this.entities.y[slot] = ty
      this.entities.vx[slot] = 0
      this.entities.vy[slot] = 0
      this.unitCell[slot] = this.world.cellAt(tx, ty)
      this.goal[slot] = NO_CELL
      // A returning aircraft is handed to the rearm cycle rather than going
      // idle over its own airbase, and never diverted onto a leftover route.
      if (this.entities.state[slot] === ST.RearmingInbound) return
      if (this.advanceRoute(slot)) return
      this.entities.state[slot] = ST.Idle
      return
    }

    const stepX = fix.div(fix.mul(dx, speed), d)
    const stepY = fix.div(fix.mul(dy, speed), d)
    this.entities.x[slot] = fix.add(fix.raw(this.entities.x[slot]), stepX)
    this.entities.y[slot] = fix.add(fix.raw(this.entities.y[slot]), stepY)
    this.entities.vx[slot] = stepX
    this.entities.vy[slot] = stepY
    this.entities.heading[slot] = fix.atan2(dy, dx)
    this.unitCell[slot] = this.world.cellAt(
      fix.raw(this.entities.x[slot]),
      fix.raw(this.entities.y[slot]),
    )
  }

  /** Chebyshev distance within the arrival radius. */
  private withinArrival(cell: number, goal: number, radius: number): boolean {
    const dx = Math.abs((cell % MAP_SIZE) - (goal % MAP_SIZE))
    const dy = Math.abs(((cell / MAP_SIZE) | 0) - ((goal / MAP_SIZE) | 0))
    return Math.max(dx, dy) <= radius
  }

  private atCellCentre(slot: number, cell: number): boolean {
    const cx = this.world.cellCenterX(cell % MAP_SIZE)
    const cy = this.world.cellCenterY((cell / MAP_SIZE) | 0)
    const dx = fix.sub(cx, fix.raw(this.entities.x[slot]))
    const dy = fix.sub(cy, fix.raw(this.entities.y[slot]))
    return fix.abs(dx) <= ARRIVE_EPSILON && fix.abs(dy) <= ARRIVE_EPSILON
  }

  /** Slide toward the centre of the cell this unit already owns. */
  private slide(slot: number, cell: number): void {
    const speed = this.speedOf(slot)
    const tx = this.world.cellCenterX(cell % MAP_SIZE)
    const ty = this.world.cellCenterY((cell / MAP_SIZE) | 0)
    const dx = fix.sub(tx, fix.raw(this.entities.x[slot]))
    const dy = fix.sub(ty, fix.raw(this.entities.y[slot]))
    const d = fix.length(dx, dy)

    if (d <= speed || d === 0) {
      this.entities.x[slot] = tx
      this.entities.y[slot] = ty
      this.entities.vx[slot] = 0
      this.entities.vy[slot] = 0
      return
    }
    const stepX = fix.div(fix.mul(dx, speed), d)
    const stepY = fix.div(fix.mul(dy, speed), d)
    this.entities.x[slot] = fix.add(fix.raw(this.entities.x[slot]), stepX)
    this.entities.y[slot] = fix.add(fix.raw(this.entities.y[slot]), stepY)
    this.entities.vx[slot] = stepX
    this.entities.vy[slot] = stepY
    this.entities.heading[slot] = fix.atan2(dy, dx)
  }

  private arrive(slot: number): void {
    if (!this.atCellCentre(slot, this.unitCell[slot])) {
      this.slide(slot, this.unitCell[slot])
      return
    }
    this.goal[slot] = NO_CELL
    this.fieldOf[slot] = NO_CELL
    this.wait[slot] = 0
    this.fails[slot] = 0
    this.entities.vx[slot] = 0
    this.entities.vy[slot] = 0
    if (this.advanceRoute(slot)) return
    if (this.entities.state[slot] === ST.Moving || this.entities.state[slot] === ST.AttackMoving ||
        this.entities.state[slot] === ST.Scattering) {
      this.entities.state[slot] = ST.Idle
    }
  }

  /** Next cell along the flow field, or the local overlay after a wait. */
  private nextCell(slot: number, from: number, goal: number): number {
    const field = this.fieldFor(slot, goal)
    if (!field) return NO_CELL
    const base = slot * MAX_DETOUR
    while (this.detourLen[slot] > 0 && this.detourSteps[base + this.detourLen[slot] - 1] === from) {
      this.detourLen[slot]--
    }
    if (this.detourLen[slot] > 0) {
      const next = this.detourSteps[base + this.detourLen[slot] - 1]
      const occupant = this.cellUnit[next] - 1
      const x = from % MAP_SIZE
      const y = (from / MAP_SIZE) | 0
      const nx = next % MAP_SIZE
      const ny = (next / MAP_SIZE) | 0
      if (this.isPassable(next) && (x === nx || y === ny || this.diagonalAllowed(x, y, nx, ny)) &&
          (occupant < 0 || this.goal[occupant] !== NO_CELL)) return next
      this.detourLen[slot] = 0
      return this.localStep(slot, from, field)
    }

    if (this.wait[slot] < WAIT_BEFORE_REPATH) {
      const dir = field.dir[from]
      if (dir === NO_DIR) return NO_CELL
      const nx = (from % MAP_SIZE) + DX[dir]
      const ny = ((from / MAP_SIZE) | 0) + DY[dir]
      if (nx < 0 || ny < 0 || nx >= MAP_SIZE || ny >= MAP_SIZE) return NO_CELL
      return ny * MAP_SIZE + nx
    }

    /**
     * The unit has waited long enough that the shared field is not going to
     * help. Because the field is shared and the simulation deterministic, a
     * unit that merely waits and retries re-derives the identical blocked
     * claim forever. So the retry is computed against a **local
     * occupancy-aware overlay** instead — the second attempt can actually
     * differ from the first.
     */
    return this.localStep(slot, from, field)
  }

  /** Keep a bounded occupancy-aware detour until it rejoins the shared flow. */
  private localStep(slot: number, from: number, field: FlowField): number {
    const radius = 10
    const side = radius * 2 + 1
    const originX = from % MAP_SIZE
    const originY = (from / MAP_SIZE) | 0
    this.localParents.fill(-1)
    this.localQueue[0] = from
    this.localParents[radius * side + radius] = from
    let head = 0
    let tail = 1
    while (head < tail) {
      const cell = this.localQueue[head++]
      const x = cell % MAP_SIZE
      const y = (cell / MAP_SIZE) | 0
      if (cell !== from && field.dist[cell] < field.dist[from]) {
        let cursor = cell
        let length = 0
        const base = slot * MAX_DETOUR
        while (cursor !== from && length < MAX_DETOUR) {
          this.detourSteps[base + length++] = cursor
          const local = (((cursor / MAP_SIZE) | 0) - originY + radius) * side + cursor % MAP_SIZE - originX + radius
          cursor = this.localParents[local]
        }
        if (cursor !== from) return NO_CELL
        this.detourLen[slot] = length
        return this.detourSteps[base + length - 1]
      }
      for (let d = 0; d < 8; d++) {
        const nx = x + DX[d]
        const ny = y + DY[d]
        if (nx < 0 || ny < 0 || nx >= MAP_SIZE || ny >= MAP_SIZE ||
            Math.abs(nx - originX) > radius || Math.abs(ny - originY) > radius) continue
        const next = ny * MAP_SIZE + nx
        const local = (ny - originY + radius) * side + nx - originX + radius
        if (this.localParents[local] !== -1 || !this.isPassable(next) || this.cellUnit[next] !== 0) continue
        if (d >= 4 && !this.diagonalAllowed(x, y, nx, ny)) continue
        this.localParents[local] = cell
        this.localQueue[tail++] = next
      }
    }
    return NO_CELL
  }

  private scatterStep(from: number): number {
    const fx = from % MAP_SIZE
    const fy = (from / MAP_SIZE) | 0
    // Deterministic pseudo-random direction from the simulation RNG.
    const start = this.ctx.rng.int(8)
    for (let k = 0; k < 8; k++) {
      const d = (start + k) % 8
      const nx = fx + DX[d]
      const ny = fy + DY[d]
      if (!this.isCellFree(nx, ny)) continue
      if (d >= 4 && !this.diagonalAllowed(fx, fy, nx, ny)) continue
      return ny * MAP_SIZE + nx
    }
    return NO_CELL
  }

  /** Diagonal moves may not cut the corner of an impassable cell. */
  private diagonalAllowed(fx: number, fy: number, nx: number, ny: number): boolean {
    return this.isPassable(fy * MAP_SIZE + nx) && this.isPassable(ny * MAP_SIZE + fx)
  }

  /**
   * Competing claims resolve in **ascending slot order**, but each waiting unit
   * carries a wait counter that raises its claim priority — otherwise high-slot
   * units are starved indefinitely at a choke, which is the standard failure of
   * a naive slot-order rule.
   */
  private resolveClaims(): void {
    for (const slot of this.movers) this.claim[this.desired[slot]] = 0
    for (const slot of this.movers) {
      const cell = this.desired[slot]
      const held = this.claim[cell] - 1
      if (held < 0) {
        this.claim[cell] = slot + 1
      } else if (this.wait[slot] > this.wait[held]) {
        this.claim[cell] = slot + 1
      }
      // Equal wait: the lower slot keeps it, because it bid first.
    }
  }

  private commitMoves(): void {
    // Several passes so a queue of units advances together: once the unit in
    // front vacates, the one behind can take its cell in the same tick.
    for (let pass = 0; pass < 8; pass++) {
      let progressed = false
      for (const slot of this.movers) {
        const target = this.desired[slot]
        if (target === NO_CELL) continue
        if (this.claim[target] !== slot + 1) continue

        const occupant = this.cellUnit[target] - 1
        if (occupant === slot) {
          this.desired[slot] = NO_CELL
          continue
        }

        if (occupant < 0) {
          this.commit(slot, target)
          progressed = true
          continue
        }

        // Two units claiming each other's current cells are a swap, and must
        // execute atomically — otherwise both queue forever, each waiting for
        // the other to vacate first.
        if (this.desired[occupant] === this.unitCell[slot] && this.claim[this.unitCell[slot]] === occupant + 1) {
          const a = this.unitCell[slot]
          const b = target
          this.cellUnit[a] = occupant + 1
          this.cellUnit[b] = slot + 1
          this.unitCell[slot] = b
          this.unitCell[occupant] = a
          this.desired[slot] = NO_CELL
          this.desired[occupant] = NO_CELL
          this.wait[slot] = 0
          this.wait[occupant] = 0
          progressed = true
        }
      }
      if (!progressed) break
    }

    // Anyone still holding a desire this tick was blocked.
    for (const slot of this.movers) {
      if (this.desired[slot] === NO_CELL) continue
      this.blocked(slot)
    }
    for (const slot of this.movers) this.desired[slot] = NO_CELL
  }

  private commit(slot: number, target: number): void {
    const from = this.unitCell[slot]
    if (from !== NO_CELL && this.cellUnit[from] === slot + 1) this.cellUnit[from] = 0
    this.cellUnit[target] = slot + 1
    this.unitCell[slot] = target
    this.desired[slot] = NO_CELL
    this.wait[slot] = 0
    this.fails[slot] = 0

    const tx = this.world.cellCenterX(target % MAP_SIZE)
    const ty = this.world.cellCenterY((target / MAP_SIZE) | 0)
    this.entities.heading[slot] = fix.atan2(
      fix.sub(ty, fix.raw(this.entities.y[slot])),
      fix.sub(tx, fix.raw(this.entities.x[slot])),
    )
    this.slide(slot, target)
  }

  private blocked(slot: number): void {
    this.wait[slot]++
    this.entities.vx[slot] = 0
    this.entities.vy[slot] = 0
    if (this.wait[slot] < WAIT_BEFORE_REPATH * 2) return

    this.wait[slot] = 0
    this.fails[slot]++
    if (this.fails[slot] >= FAILS_BEFORE_SCATTER) {
      this.fails[slot] = 0
      this.beginScatter(slot)
    }
  }

  private speedOf(slot: number): fix.Fixed {
    const def = defById(this.entities.kind[slot])
    return def ? def.speed : fix.fromFloat(0.04)
  }

  // --- flow fields ---------------------------------------------------------

  private fieldFor(slot: number, goal: number): FlowField | null {
    const key = this.fieldOf[slot]
    const existing = key !== NO_CELL ? this.fields.get(key) : undefined
    if (existing && existing.generation === this.passabilityGeneration && existing.dest === goal) {
      existing.lastUsed = this.ctx.time.tick
      return existing
    }
    const field = this.acquireField(goal)
    if (field) this.fieldOf[slot] = goal
    return field
  }

  /**
   * Build or reuse the flow field for a destination.
   *
   * A destination inside a structure's footprint resolves to the nearest
   * passable cell rather than failing. Ordering an attack onto an enemy base
   * names a cell in the middle of a 3×3 building, and every cell around it is
   * inside the same building — treating that as "unreachable" left every AI
   * army standing in its own base for the whole match.
   */
  acquireField(destCell: number): FlowField | null {
    this.pathRequests++
    const dest = this.isPassable(destCell) ? destCell : this.nearestPassable(destCell, 8)
    if (dest < 0) return null

    const cached = this.fields.get(dest)
    if (cached && cached.generation === this.passabilityGeneration) {
      cached.lastUsed = this.ctx.time.tick
      return cached
    }

    if (this.fields.size >= this.maxFields) this.evictOldestField()

    const field: FlowField = {
      dest,
      dist: new Int32Array(MAP_SIZE * MAP_SIZE),
      dir: new Uint8Array(MAP_SIZE * MAP_SIZE),
      generation: this.passabilityGeneration,
      lastUsed: this.ctx.time.tick,
      refs: 0,
    }
    this.buildField(field)
    this.fields.set(dest, field)
    return field
  }

  /** Nearest passable cell to `cell`, searched in rings. -1 if none in range. */
  nearestPassable(cell: number, maxRadius = 8): number {
    if (cell < 0 || cell >= MAP_SIZE * MAP_SIZE) return -1
    if (this.isPassable(cell)) return cell
    const sx = cell % MAP_SIZE
    const sy = (cell / MAP_SIZE) | 0
    for (let r = 1; r <= maxRadius; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx > -r && dx < r && dy > -r && dy < r) continue
          const x = sx + dx
          const y = sy + dy
          if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) continue
          if (this.isPassable(y * MAP_SIZE + x)) return y * MAP_SIZE + x
        }
      }
    }
    return -1
  }

  private evictOldestField(): void {
    let oldestKey = -1
    let oldest = Infinity
    // Iterating a Map, but only to pick a cache victim — the choice affects
    // nothing but memory, never simulation state, because a field is a pure
    // function of its destination and the passability generation.
    for (const [key, f] of this.fields) {
      if (f.lastUsed < oldest) {
        oldest = f.lastUsed
        oldestKey = key
      }
    }
    if (oldestKey >= 0) this.fields.delete(oldestKey)
  }

  /** Dijkstra outward from the destination over passable cells. */
  private buildField(field: FlowField): void {
    this.fieldBuilds++
    const dist = field.dist
    const dir = field.dir
    dist.fill(UNREACHABLE)
    dir.fill(NO_DIR)

    this.heapSize = 0
    dist[field.dest] = 0
    this.heapPush(0, field.dest)

    while (this.heapSize > 0) {
      const top = this.heapPop()
      const cost = top[0]
      const cell = top[1]
      if (cost > dist[cell]) continue
      const cx = cell % MAP_SIZE
      const cy = (cell / MAP_SIZE) | 0

      for (let d = 0; d < 8; d++) {
        const nx = cx + DX[d]
        const ny = cy + DY[d]
        if (nx < 0 || ny < 0 || nx >= MAP_SIZE || ny >= MAP_SIZE) continue
        const ni = ny * MAP_SIZE + nx
        if (!this.isPassable(ni)) continue
        if (d >= 4 && !this.diagonalAllowed(nx, ny, cx, cy)) continue
        const next = cost + STEP_COST[d]
        if (next >= dist[ni]) continue
        dist[ni] = next
        // The neighbour steps back toward `cell`, so its direction is the
        // reverse of the one we walked outward along.
        dir[ni] = reverseDir(d)
        this.heapPush(next, ni)
      }
    }
  }

  private heapPush(cost: number, cell: number): void {
    let i = this.heapSize++
    this.heap[i * 2] = cost
    this.heap[i * 2 + 1] = cell
    while (i > 0) {
      const parent = (i - 1) >> 1
      if (this.heap[parent * 2] <= this.heap[i * 2]) break
      this.swapHeap(i, parent)
      i = parent
    }
  }

  private readonly popResult = new Int32Array(2)

  private heapPop(): Int32Array {
    this.popResult[0] = this.heap[0]
    this.popResult[1] = this.heap[1]
    this.heapSize--
    if (this.heapSize > 0) {
      this.heap[0] = this.heap[this.heapSize * 2]
      this.heap[1] = this.heap[this.heapSize * 2 + 1]
      let i = 0
      for (;;) {
        const l = i * 2 + 1
        const r = l + 1
        let smallest = i
        if (l < this.heapSize && this.heap[l * 2] < this.heap[smallest * 2]) smallest = l
        if (r < this.heapSize && this.heap[r * 2] < this.heap[smallest * 2]) smallest = r
        if (smallest === i) break
        this.swapHeap(i, smallest)
        i = smallest
      }
    }
    return this.popResult
  }

  private swapHeap(a: number, b: number): void {
    const c0 = this.heap[a * 2]
    const c1 = this.heap[a * 2 + 1]
    this.heap[a * 2] = this.heap[b * 2]
    this.heap[a * 2 + 1] = this.heap[b * 2 + 1]
    this.heap[b * 2] = c0
    this.heap[b * 2 + 1] = c1
  }

  /** Drop every cached field. Called when passability changes. */
  invalidatePassability(): void {
    this.passabilityGeneration++
    this.fields.clear()
    this.rebuildClusters()
  }

  // --- cluster graph -------------------------------------------------------

  /**
   * Label connected components inside each cluster, then union them across
   * cluster borders. Reachability becomes a union-find lookup, and rebuilding
   * after a wall falls touches only the affected clusters.
   */
  rebuildClusters(): void {
    this.component.fill(0)
    this.componentCount = 0
    const labels: number[] = [0]

    const stack = this.scratchDist // reused as a scratch stack
    for (let cy0 = 0; cy0 < MAP_SIZE; cy0 += CLUSTER_SIZE) {
      for (let cx0 = 0; cx0 < MAP_SIZE; cx0 += CLUSTER_SIZE) {
        for (let y = cy0; y < cy0 + CLUSTER_SIZE; y++) {
          for (let x = cx0; x < cx0 + CLUSTER_SIZE; x++) {
            const i = y * MAP_SIZE + x
            if (this.component[i] !== 0 || !this.isPassable(i)) continue
            const label = ++this.componentCount
            labels.push(label)
            let top = 0
            stack[top++] = i
            this.component[i] = label
            while (top > 0) {
              const c = stack[--top]
              const px = c % MAP_SIZE
              const py = (c / MAP_SIZE) | 0
              for (let d = 0; d < 4; d++) {
                const nx = px + DX[d]
                const ny = py + DY[d]
                if (nx < cx0 || ny < cy0 || nx >= cx0 + CLUSTER_SIZE || ny >= cy0 + CLUSTER_SIZE) {
                  continue
                }
                const ni = ny * MAP_SIZE + nx
                if (this.component[ni] !== 0 || !this.isPassable(ni)) continue
                this.component[ni] = label
                stack[top++] = ni
              }
            }
          }
        }
      }
    }

    this.region = new Int32Array(this.componentCount + 1)
    for (let i = 0; i <= this.componentCount; i++) this.region[i] = i

    // Union across cluster borders.
    for (let y = 0; y < MAP_SIZE; y++) {
      for (let x = 0; x < MAP_SIZE; x++) {
        const i = y * MAP_SIZE + x
        const a = this.component[i]
        if (a === 0) continue
        if (x + 1 < MAP_SIZE) {
          const b = this.component[i + 1]
          if (b !== 0) this.union(a, b)
        }
        if (y + 1 < MAP_SIZE) {
          const b = this.component[i + MAP_SIZE]
          if (b !== 0) this.union(a, b)
        }
      }
    }

    // Cells per union root. This is what lets a caller tell a doorway from a
    // sealed pocket: a pocket between buildings is a region of a handful of
    // cells, the open map is a region of thousands.
    const perLabel = new Int32Array(this.componentCount + 1)
    const total = MAP_SIZE * MAP_SIZE
    for (let i = 0; i < total; i++) {
      const c = this.component[i]
      if (c !== 0) perLabel[c]++
    }
    this.regionCells = new Int32Array(this.componentCount + 1)
    for (let label = 1; label <= this.componentCount; label++) {
      this.regionCells[this.find(label)] += perLabel[label]
    }
  }

  /** Cells in the connected region containing `cell`, or 0 if impassable. */
  regionSizeAt(cell: number): number {
    if (cell < 0) return 0
    const c = this.component[cell]
    if (c === 0) return 0
    return this.regionCells[this.find(c)]
  }

  private find(a: number): number {
    let root = a
    while (this.region[root] !== root) root = this.region[root]
    while (this.region[a] !== root) {
      const next = this.region[a]
      this.region[a] = root
      a = next
    }
    return root
  }

  private union(a: number, b: number): void {
    const ra = this.find(a)
    const rb = this.find(b)
    if (ra !== rb) this.region[ra < rb ? rb : ra] = ra < rb ? ra : rb
  }

  /**
   * Is `to` reachable from `from` over static terrain? O(1) after the cluster
   * graph is built, and the reason an unreachable order fails immediately
   * rather than after a full failed search.
   */
  isReachable(from: number, to: number): boolean {
    if (from < 0 || to < 0) return false
    const a = this.component[from]
    const b = this.component[to]
    if (a === 0 || b === 0) return false
    return this.find(a) === this.find(b)
  }

  /** Number of distinct connected regions. Exposed for tests. */
  regionCount(): number {
    const roots = new Set<number>()
    for (let i = 1; i <= this.componentCount; i++) roots.add(this.find(i))
    return roots.size
  }

  dispose(): void {
    this.fields.clear()
  }
}

/** The direction that walks back the way `d` came. */
function reverseDir(d: number): number {
  switch (d) {
    case 0:
      return 1
    case 1:
      return 0
    case 2:
      return 3
    case 3:
      return 2
    case 4:
      return 7
    case 5:
      return 6
    case 6:
      return 5
    default:
      return 4
  }
}
